import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useCallback, useEffect, useMemo, useState } from "react";

interface ControleAcesso {
  id: number;
  id_perfil_acesso: number;
  id_menu: number;
  visualizar: boolean;
  criar: boolean;
  editar: boolean;
  excluir: boolean;
  relatorio: boolean;
  menu?: {
    id: number;
    nome: string;
    rota?: string;
  };
}

interface PermissionsCache {
  [menuId: number]: ControleAcesso;
}

export function usePermissions() {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<PermissionsCache>({});
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Verificar se o usuário é admin
  const checkIsAdmin = useCallback(async () => {
    if (!user?.id_perfil_acesso) {
      setIsAdmin(false);
      return false;
    }

    try {
      const response = await api.get(`/api/perfil-acesso/${user.id_perfil_acesso}`);
      if (response?.result) {
        const perfilNome = response.result.nome?.toUpperCase();
        const admin = perfilNome === "ADMIN";
        setIsAdmin(admin);
        return admin;
      }
    } catch (error) {
      console.error("Erro ao verificar perfil admin:", error);
    }
    
    setIsAdmin(false);
    return false;
  }, [user?.id_perfil_acesso]);

  // Carregar permissões do usuário
  const loadPermissions = useCallback(async () => {
    if (!user?.id_perfil_acesso) {
      setPermissions({});
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Verificar se é admin primeiro
      const admin = await checkIsAdmin();
      
      if (admin) {
        // Admin tem todas as permissões, não precisa carregar
        setPermissions({});
        setLoading(false);
        return;
      }

      // Buscar controles de acesso do perfil
      const response = await api.get("/api/controle-acesso");
      const cache: PermissionsCache = {};

      if (response?.result) {
        const controles = Array.isArray(response.result) ? response.result : [];
        const idPerfil = Number(user.id_perfil_acesso);
        const userPermissions = controles.filter(
          (ca: ControleAcesso) => Number(ca.id_perfil_acesso) === idPerfil
        );
        userPermissions.forEach((ca: ControleAcesso) => {
          cache[ca.id_menu] = ca;
        });
      }

      // Se o perfil for PACIENTE, incluir menus com habilitar_para_paciente = true
      const perfilRes = await api.get(`/api/perfil-acesso/${user.id_perfil_acesso}`);
      const perfilNome = (perfilRes?.result as { nome?: string })?.nome?.toUpperCase() || "";
      if (perfilNome === "PACIENTE") {
        const menusRes = await api.get("/api/menu_sistema");
        const menus = (menusRes?.result || []) as Array<{ id: number; habilitar_para_paciente?: boolean }>;
        menus.forEach((menu) => {
          if (menu.habilitar_para_paciente) {
            cache[menu.id] = {
              id: 0,
              id_perfil_acesso: user.id_perfil_acesso,
              id_menu: menu.id,
              visualizar: true,
              criar: false,
              editar: false,
              excluir: false,
              relatorio: false,
            };
          }
        });
      }

      setPermissions(cache);
    } catch (error) {
      console.error("Erro ao carregar permissões:", error);
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, [user?.id_perfil_acesso, checkIsAdmin]);

  // Carregar permissões quando o usuário mudar
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Verificar se tem permissão de visualizar um menu
  const hasMenuAccess = useCallback(
    (menuId: number): boolean => {
      if (isAdmin) return true;
      if (!user?.id_perfil_acesso) return false;
      
      const controle = permissions[menuId];
      // Aceita true ou 1 (MySQL pode retornar boolean como 0/1)
      return Boolean(controle?.visualizar);
    },
    [isAdmin, permissions, user?.id_perfil_acesso]
  );

  // Verificar se tem permissão específica em um menu
  const hasPermission = useCallback(
    (
      menuId: number,
      permission: "visualizar" | "criar" | "editar" | "excluir" | "relatorio"
    ): boolean => {
      if (isAdmin) return true;
      if (!user?.id_perfil_acesso) return false;
      
      const controle = permissions[menuId];
      return controle?.[permission] === true;
    },
    [isAdmin, permissions, user?.id_perfil_acesso]
  );

  // Verificar múltiplas permissões (retorna true se tiver pelo menos uma)
  const hasAnyPermission = useCallback(
    (
      menuId: number,
      permissionsList: Array<"visualizar" | "criar" | "editar" | "excluir" | "relatorio">
    ): boolean => {
      if (isAdmin) return true;
      if (!user?.id_perfil_acesso) return false;
      
      const controle = permissions[menuId];
      if (!controle) return false;
      
      return permissionsList.some((perm) => controle[perm] === true);
    },
    [isAdmin, permissions, user?.id_perfil_acesso]
  );

  // Verificar se tem todas as permissões especificadas
  const hasAllPermissions = useCallback(
    (
      menuId: number,
      permissionsList: Array<"visualizar" | "criar" | "editar" | "excluir" | "relatorio">
    ): boolean => {
      if (isAdmin) return true;
      if (!user?.id_perfil_acesso) return false;
      
      const controle = permissions[menuId];
      if (!controle) return false;
      
      return permissionsList.every((perm) => controle[perm] === true);
    },
    [isAdmin, permissions, user?.id_perfil_acesso]
  );

  // Obter permissões de um menu específico
  const getMenuPermissions = useCallback(
    (menuId: number): ControleAcesso | null => {
      if (isAdmin) {
        // Retorna um objeto com todas as permissões como true para admin
        return {
          id: 0,
          id_perfil_acesso: user?.id_perfil_acesso || 0,
          id_menu: menuId,
          visualizar: true,
          criar: true,
          editar: true,
          excluir: true,
          relatorio: true,
        };
      }
      
      return permissions[menuId] || null;
    },
    [isAdmin, permissions, user?.id_perfil_acesso]
  );

  // Recarregar permissões manualmente
  const refreshPermissions = useCallback(() => {
    loadPermissions();
  }, [loadPermissions]);

  return {
    isAdmin,
    loading,
    permissions,
    hasMenuAccess,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getMenuPermissions,
    refreshPermissions,
  };
}

